import { Add as AddIcon, LocalHospitalOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseApiError } from '../../../library/apiErrorHelper';
import { ClinicFormValues, IClinic, IClinicDomain, IClinicMember, InviteAdminFormValues, primaryDomainLabel, toCreateClinicRequest, toInviteAdminRequest, toUpdateClinicRequest } from '../../../library/models/clinic';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { InviteAdminVariables, UpdateClinicVariables } from '../../../services/clinicsService';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { RecordCard } from '../../recordCard/RecordCard';
import { cardListStyles } from '../../recordCard/recordCard.styles';
import { ClinicFormDialog } from './ClinicFormDialog';
import { ClinicMembersDialog } from './ClinicMembersDialog';
import { InviteAdminDialog } from './InviteAdminDialog';
import { actionsCellStyles, actionsHeaderCellStyles, alertStyles, emptyStateBoxStyles, emptyStateIconStyles, headerBoxStyles, headerCellStyles, identifierCellStyles, loadingBoxStyles, rowActionsBoxStyles, rowStyles, subtitleStyles, tableContainerStyles, titleStyles } from './clinics.styles';

/**
 * The platform owner's clinic registry.
 *
 * A table where there is room for one: the row carries a name, two identifiers -- the slug
 * and the clinic's public hostname -- and a status, and identifiers exist to be compared
 * across rows, which is what a column makes possible.
 *
 * Below `md` there is no such room, and the domain column only widened the case: the columns
 * now want rather more than the 762px they wanted before, against a container that is the
 * viewport less 48, so a narrow screen drew the three row actions a screen and a half past
 * the right edge -- and deactivating a clinic is only reachable from there. Under `md` the
 * same rows are drawn as `RecordCard`s, where the actions sit under the facts instead of
 * beyond them.
 *
 * Deleting a clinic is deactivating it. The API has no delete, on purpose: a clinic owns
 * services, memberships and appointments, so it is retired rather than removed, and the
 * list keeps showing it so it can be brought back.
 */
export const Clinics = (): JSX.Element => {
    const theme = useTheme();
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isCompact = useIsCompact();

    const { data, isLoading, refetch } = useReactQuery<IClinic[]>(
        services.clinics.getClinics()
    );

    const createClinic = useReactMutation(services.clinics.createClinicMutation());
    const updateClinic = useReactMutation<IClinic, Error, UpdateClinicVariables>(
        services.clinics.updateClinicMutation()
    );

    const [isFormOpen, setIsFormOpen] = useState(false);
    /** The clinic being edited, or null while creating. */
    const [clinicUnderEdit, setClinicUnderEdit] = useState<IClinic | null>(null);
    /** The clinic whose active flag is about to be flipped. */
    const [clinicToToggle, setClinicToToggle] = useState<IClinic | null>(null);
    /** The clinic whose members are on screen, or null while the dialog is closed. */
    const [membersClinic, setMembersClinic] = useState<IClinic | null>(null);
    const [isInviteOpen, setIsInviteOpen] = useState(false);

    // The members query lives here rather than in the dialog so that a successful
    // invitation can refetch it while the dialog stays open.
    const {
        data: membersData,
        isLoading: isLoadingMembers,
        refetch: refetchMembers,
    } = useReactQuery<IClinicMember[]>(
        services.clinics.getClinicMembers(membersClinic?.id ?? 0),
        { enabled: membersClinic !== null }
    );

    const inviteAdmin = useReactMutation<IClinicMember, Error, InviteAdminVariables>(
        services.clinics.inviteClinicAdminMutation()
    );

    /**
     * Whether the list was actually fetched, which is not the same as "isError is false".
     * A query can also settle without data and without an error -- React Query pauses one
     * it believes cannot reach the network -- and treating that as an empty result would
     * tell the owner their platform has no clinics when nothing was ever loaded.
     */
    const hasLoaded = data !== undefined;
    const clinics = data ?? [];

    const openCreateForm = (): void => {
        setClinicUnderEdit(null);
        setIsFormOpen(true);
    };

    const openEditForm = (clinic: IClinic): void => {
        setClinicUnderEdit(clinic);
        setIsFormOpen(true);
    };

    /**
     * A domain was added, removed or re-pointed while the edit dialog is open.
     *
     * Only refetches the list, so the row behind the dialog picks up its new address. It
     * deliberately does not touch `clinicUnderEdit`: the dialog reseeds its fields whenever
     * that changes, which would throw away a display name half typed. The panel holds the
     * updated list itself, and reopening reads a fresh row from the refetched list.
     */
    const onDomainsChanged = (_domains: IClinicDomain[]): void => {
        refetch();
    };

    /**
     * Rejects on failure rather than handling it: the dialog owns the mapping from an API
     * error back onto its own fields, and only a success closes it.
     */
    const submitForm = async (values: ClinicFormValues): Promise<void> => {
        if (clinicUnderEdit) {
            await updateClinic.mutateAsync({
                id: clinicUnderEdit.id,
                payload: toUpdateClinicRequest(values),
            });
        } else {
            await createClinic.mutateAsync(toCreateClinicRequest(values));
        }

        showSnackbar(
            translate(clinicUnderEdit ? ResourcesIds.CLINIC_UPDATED_SUCCESS : ResourcesIds.CLINIC_CREATED_SUCCESS),
            'success'
        );
        setIsFormOpen(false);
        refetch();
    };

    /**
     * Rejects on failure, like submitForm: the invite dialog maps a rejected address back
     * onto its own field. On success the members list is refetched so the new invitation
     * shows up as pending straight away -- which is the only feedback that it was sent.
     */
    const submitInvite = async (values: InviteAdminFormValues): Promise<void> => {
        if (!membersClinic) {
            return;
        }

        await inviteAdmin.mutateAsync({
            clinicId: membersClinic.id,
            payload: toInviteAdminRequest(values),
        });

        showSnackbar(
            translate(ResourcesIds.INVITE_ADMIN_SUCCESS, { emailAddress: values.emailAddress.trim() }),
            'success'
        );
        setIsInviteOpen(false);
        refetchMembers();
    };

    const confirmStatusChange = (): void => {
        if (!clinicToToggle) {
            return;
        }

        const clinic = clinicToToggle;
        const nextActive = !clinic.active;

        updateClinic.mutate(
            { id: clinic.id, payload: { active: nextActive } },
            {
                onSuccess: () => {
                    showSnackbar(
                        translate(nextActive ? ResourcesIds.CLINIC_REACTIVATED_SUCCESS : ResourcesIds.CLINIC_DEACTIVATED_SUCCESS),
                        'success'
                    );
                    refetch();
                },
                onError: (error: unknown) => {
                    const parsed = parseApiError(error);
                    showSnackbar(parsed.message ?? translate(ResourcesIds.CLINIC_STATUS_CHANGE_ERROR), 'error');
                },
            }
        );

        setClinicToToggle(null);
    };

    const isReactivating = clinicToToggle !== null && !clinicToToggle.active;

    /*
     * The two pieces the table and the card list both draw. Shared as locals rather than
     * written twice: a row and a card describing the same clinic must offer the same three
     * actions, and the surest way to keep them offering the same three is for there to
     * only be one place that says what they are.
     */

    /** The clinic's address, abbreviated when it answers on more than one. */
    const domainLabelFor = (clinic: IClinic): string =>
        primaryDomainLabel(clinic, translate(ResourcesIds.CLINIC_DOMAIN_MORE_SUFFIX))
            || translate(ResourcesIds.CLINIC_DOMAIN_NONE);

    const statusChipFor = (clinic: IClinic): JSX.Element => (
        <Chip
            size="small"
            color={clinic.active ? 'success' : 'default'}
            label={translate(clinic.active ? ResourcesIds.CLINIC_STATUS_ACTIVE : ResourcesIds.CLINIC_STATUS_INACTIVE)}
        />
    );

    const actionsFor = (clinic: IClinic): JSX.Element => (
        <>
            <Button size="small" onClick={() => openEditForm(clinic)}>
                {translate(ResourcesIds.EDIT_TEXT)}
            </Button>
            <Button size="small" onClick={() => setMembersClinic(clinic)}>
                {translate(ResourcesIds.CLINIC_MEMBERS_ACTION)}
            </Button>
            <Button
                size="small"
                color={clinic.active ? 'error' : 'primary'}
                onClick={() => setClinicToToggle(clinic)}
            >
                {translate(clinic.active ? ResourcesIds.DEACTIVATE_CLINIC_ACTION : ResourcesIds.REACTIVATE_CLINIC_ACTION)}
            </Button>
        </>
    );

    return (
        <Box>
            <Box sx={headerBoxStyles}>
                <Box>
                    <Typography variant="h4" component="h1" sx={titleStyles}>
                        {translate(ResourcesIds.CLINICS_TITLE)}
                    </Typography>
                    <Typography variant="body1" sx={subtitleStyles}>
                        {translate(ResourcesIds.CLINICS_SUBTITLE)}
                    </Typography>
                </Box>

                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateForm}>
                    {translate(ResourcesIds.ADD_CLINIC_BUTTON)}
                </Button>
            </Box>

            {!isLoading && !hasLoaded && (
                <Alert severity="error" sx={alertStyles}>
                    {translate(ResourcesIds.CLINICS_LOAD_ERROR)}
                </Alert>
            )}

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress size={60} />
                </Box>
            ) : !hasLoaded ? null : clinics.length === 0 ? (
                <Box sx={emptyStateBoxStyles}>
                    <LocalHospitalOutlined sx={emptyStateIconStyles(theme)} />
                    <Typography variant="h6" gutterBottom>
                        {translate(ResourcesIds.CLINICS_EMPTY_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.CLINICS_EMPTY_SUBTITLE)}
                    </Typography>
                </Box>
            ) : isCompact ? (
                <Box sx={cardListStyles}>
                    {clinics.map(clinic => (
                        <RecordCard
                            key={clinic.id}
                            title={clinic.displayName}
                            titleAdornment={statusChipFor(clinic)}
                            isMuted={!clinic.active}
                            fields={[
                                {
                                    label: translate(ResourcesIds.CLINIC_SLUG_LABEL),
                                    value: clinic.slug,
                                },
                                {
                                    label: translate(ResourcesIds.CLINIC_DOMAIN_LABEL),
                                    value: domainLabelFor(clinic),
                                },
                            ]}
                            actions={actionsFor(clinic)}
                        />
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} sx={tableContainerStyles}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyles}>{translate(ResourcesIds.CLINIC_DISPLAY_NAME_LABEL)}</TableCell>
                                <TableCell sx={headerCellStyles}>{translate(ResourcesIds.CLINIC_SLUG_LABEL)}</TableCell>
                                <TableCell sx={headerCellStyles}>{translate(ResourcesIds.CLINIC_DOMAIN_LABEL)}</TableCell>
                                <TableCell sx={headerCellStyles}>{translate(ResourcesIds.CLINIC_STATUS_LABEL)}</TableCell>
                                <TableCell sx={actionsHeaderCellStyles}>
                                    {translate(ResourcesIds.CLINIC_ACTIONS_LABEL)}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {clinics.map(clinic => (
                                <TableRow key={clinic.id} sx={rowStyles(clinic.active)}>
                                    <TableCell>{clinic.displayName}</TableCell>
                                    <TableCell sx={identifierCellStyles}>{clinic.slug}</TableCell>
                                    <TableCell sx={identifierCellStyles}>{domainLabelFor(clinic)}</TableCell>
                                    <TableCell>{statusChipFor(clinic)}</TableCell>
                                    <TableCell sx={actionsCellStyles}>
                                        <Box sx={rowActionsBoxStyles}>{actionsFor(clinic)}</Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ClinicFormDialog
                isOpen={isFormOpen}
                clinic={clinicUnderEdit}
                onClose={() => setIsFormOpen(false)}
                onSubmit={submitForm}
                onDomainsChanged={onDomainsChanged}
            />

            <ClinicMembersDialog
                isOpen={membersClinic !== null}
                clinic={membersClinic}
                members={membersData ?? []}
                isLoading={isLoadingMembers}
                hasLoaded={membersData !== undefined}
                onClose={() => setMembersClinic(null)}
                onInviteClick={() => setIsInviteOpen(true)}
            />

            <InviteAdminDialog
                isOpen={isInviteOpen}
                clinicName={membersClinic?.displayName ?? ''}
                onClose={() => setIsInviteOpen(false)}
                onSubmit={submitInvite}
            />

            <ConfirmationDialog
                isOpen={clinicToToggle !== null}
                onClose={() => setClinicToToggle(null)}
                onConfirm={confirmStatusChange}
                title={translate(isReactivating ? ResourcesIds.REACTIVATE_CLINIC_TITLE : ResourcesIds.DEACTIVATE_CLINIC_TITLE)}
                description={translate(isReactivating ? ResourcesIds.REACTIVATE_CLINIC_DESCRIPTION : ResourcesIds.DEACTIVATE_CLINIC_DESCRIPTION)}
                confirmMessage={translate(isReactivating ? ResourcesIds.REACTIVATE_CLINIC_CONFIRM : ResourcesIds.DEACTIVATE_CLINIC_CONFIRM)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </Box>
    );
};
